import React from "react";

import {
  AppsIcon,
  DnsIcon,
  DownloadIcon,
  ImageArrowIcon,
  LiveTvIcon,
  ManageAccountsIcon,
  MonitorHeartIcon,
  OpenInNewIcon,
  QuestionMarkIcon,
  SpeedIcon,
  StrategiesIcon,
} from "veneer/SvgIcon";

import { ENV } from "cortex/ui/src/constant/app";

import type { NavItemData } from "./NavBar";
import { ROUTE_IDS } from "./routeIds";

const { FLEET_URL } = ENV;

export const getFleetRouteItems = (
  featureFlags?: Record<string, boolean>,
): NavItemData[] => {
  return [
    {
      icon: <SpeedIcon />,
      id: ROUTE_IDS.Overview,
      label: "Overview",
      route: "/overview",
    },
    {
      children: [
        {
          altLabel: "Devices",
          id: ROUTE_IDS.DevicesList,
          label: "List",
          matchingPaths: [
            "/devices/:deviceId(d-[a-f0-9-]+)",
            "/devices/:deviceId(d-[a-f0-9-]+)/flags",
            "/devices/:deviceId(d-[a-f0-9-]+)/configure",
            "/devices/:deviceId(d-[a-f0-9-]+)/edit",
            "/devices/:deviceId(d-[a-f0-9-]+)/:activeTab(overview|events|details|internals|logs)",
          ],
          route: "/devices",
        },
        {
          id: ROUTE_IDS.DevicesBulkEdit,
          label: "Bulk Edit",
          route: "/devices/bulk-edit",
        },
      ],
      icon: <LiveTvIcon />,
      id: ROUTE_IDS.Devices,
      label: "Devices",
      route: "/devices",
    },
    {
      children: [
        {
          altLabel: "Strategies",
          id: ROUTE_IDS.StrategiesList,
          label: "List",
          matchingPaths: ["/v2/strategies/:id([0-9]+)/:viewType(clone|edit)"],
          route: "/v2/strategies",
        },
        {
          id: ROUTE_IDS.StrategiesNew,
          label: "New",
          route: `/v2/strategies/create`,
        },
        {
          id: ROUTE_IDS.StrategiesMediaLibrary,
          label: "Media Library",
          matchingPaths: ["/v2/strategies/media-library/folders/:id"],
          route: "/v2/strategies/media-library",
        },
        {
          id: ROUTE_IDS.StrategiesDeploy,
          label: "Deploy Strategy",
          route: "/v2/strategies/deploy",
        },
        {
          id: ROUTE_IDS.StrategiesScheduled,
          label: "Scheduled Deployments",
          route: "/v2/strategies/scheduled",
        },
        {
          id: ROUTE_IDS.StrategiesPast,
          label: "Past Deployments",
          route: "/v2/strategies/deployments",
        },
      ],
      hidden: !featureFlags?.["is-strategies-enabled"],
      icon: <StrategiesIcon />,
      id: ROUTE_IDS.Strategies,
      label: "Strategies",
      route: "/v2/strategies",
    },
    {
      children: [
        {
          id: ROUTE_IDS.ApplicationsInstalled,
          label: "Installed Apps",
          matchingPaths: [
            "/applications/:id([0-9]+)",
            "/applications/:id([0-9]+)/:mode(testing)",
          ],
          route: "/applications",
        },
        {
          id: ROUTE_IDS.ApplicationsUpload,
          label: "Upload Application",
          route: "/applications/upload",
        },
        {
          id: ROUTE_IDS.ApplicationsStore,
          label: "App Store",
          route: "/applications/store",
        },
        {
          hidden: featureFlags?.["is-strategies-enabled"],
          id: ROUTE_IDS.StrategiesMediaLibrary,
          label: "Fallback Creatives",
          matchingPaths: ["/v2/strategies/media-library/folders/:id"],
          route: "/v2/strategies/media-library",
        },
      ],
      icon: featureFlags?.["is-strategies-enabled"] ? (
        <AppsIcon />
      ) : (
        <ImageArrowIcon />
      ),
      id: ROUTE_IDS.Applications,
      label: featureFlags?.["is-strategies-enabled"]
        ? "Applications"
        : "Content Sources",
      route: "/applications",
    },
    {
      icon: <DnsIcon />,
      id: ROUTE_IDS.Networks,
      label: "Networks",
      matchingPaths: [
        "/networks/:action(create)",
        "/networks/:id([0-9]+)",
        "/networks/:id([0-9]+)/configure",
        "/networks/:id([0-9]+)/flags",
        "/networks/:id([0-9]+)/edit",
      ],
      route: "/networks",
    },
    {
      children: [
        {
          id: ROUTE_IDS.DiagnosticsEvents,
          label: "Events",
          route: "/diagnostics",
        },
        {
          id: ROUTE_IDS.DiagnosticsIncidents,
          label: featureFlags?.["is-grouped-triggered-alerts"]
            ? "Incidents"
            : "Triggered Alerts",
          route: "/diagnostics/incidents",
        },
        {
          id: ROUTE_IDS.DiagnosticsAlerts,
          label: "Alerts",
          matchingPaths: [
            "/diagnostics/alerts",
            `/diagnostics/alerts/:action(create)`,
            `/diagnostics/alerts/:id/:action(edit)`,
          ],
          route: "/diagnostics/alerts",
        },
        {
          id: ROUTE_IDS.DiagnosticsUptimeReport,
          label: "Uptime Report",
          matchingPaths: [
            "/diagnostics/uptime-report/:networkId/:start/:end",
            "/diagnostics/uptime-report/:networkId/:start/:end/devices/:date",
            "/diagnostics/uptime-report/:networkId/:start/:end/device/:deviceId",
          ],
          route: "/diagnostics/uptime-report",
        },
      ],
      icon: <MonitorHeartIcon />,
      id: ROUTE_IDS.Diagnostics,
      label: "Monitoring",
      route: "/diagnostics",
    },
    {
      icon: <ManageAccountsIcon />,
      id: ROUTE_IDS.Users,
      label: "Users",
      matchingPaths: [
        "/users/:action(create)",
        "/users/:id([0-9]+)/:action(edit)",
      ],
      route: "/users",
    },
    {
      icon: <DownloadIcon />,
      id: ROUTE_IDS.Download,
      label: "Download",
      route: `${FLEET_URL}/download/`,
    },
  ];
};

export const HELP_CENTER_NAV_ITEMS: NavItemData[] = [
  {
    children: [
      {
        endAdornment: <OpenInNewIcon size="small" />,
        id: "knowledge_base",
        label: "Knowledge Base",
        route: "https://help.vistarmedia.com",
      },
      {
        endAdornment: <OpenInNewIcon size="small" />,
        id: "vistar_academy",
        label: "Vistar Academy",
        route: "https://academy.vistarmedia.com",
      },
    ],
    icon: <QuestionMarkIcon />,
    id: ROUTE_IDS.HelpCenter,
    label: "Help",
  },
];
